// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the FYI card on the phone inbox, the smarter Tidy up. Mail worth seeing
// once that needs no reply (GitHub notifications, deploy runs, digests, meeting
// replies) is grouped by kind, with what stayed in the inbox and why. A group
// opens a sheet: what happened (a short summary from the person's assistant),
// archive or keep the newest, "Always tidy" and "Not FYI". Dismissing hides the
// card until newer FYI mail arrives.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import styles from '@app/features/konvo_mail/components/MailFyiCard.module.css';
import {MailFyiMark} from '@app/features/konvo_mail/components/MailFyiMark';
import KonvoMail, {type KonvoMailMessage} from '@app/features/konvo_mail/state/KonvoMail';
import {
	firstName,
	type MailFyiCardState,
	type MailFyiGroup,
	type MailFyiRule,
} from '@app/features/konvo_mail/utils/MailFyi';
import {http} from '@app/features/platform/transport/RestTransport';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ArchiveIcon, CaretRightIcon, MagicWandIcon, SparkleIcon, TrayIcon, XIcon} from '@app/features/ui/icons/Icons';
import {MenuBottomSheet, type MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const LABEL_DESCRIPTOR = msg({
	message: 'FYI · No reply needed',
	comment: 'Label of the phone inbox card that gathers mail you only need to glance at (notifications, runs, digests).',
});
const TITLE_DESCRIPTOR = msg({
	message: '{count, plural, one {# mail you only need to glance at} other {# mails you only need to glance at}}',
	comment: 'FYI card title. {count} is how many FYI mails are in the inbox.',
});
const RUNS_DESCRIPTOR = msg({
	message: 'Deploy runs',
	comment: 'FYI card group: build and deploy run results (GitHub Actions, Vercel and the like) that passed.',
});
const DIGESTS_DESCRIPTOR = msg({
	message: 'Digests',
	comment: 'FYI card group: daily digests, newsletters and reports.',
});
const MEETING_REPLIES_DESCRIPTOR = msg({
	message: 'Meeting replies',
	comment: 'FYI card group: accepted, tentative and declined replies to meetings you organised.',
});
const ALL_PASSED_DESCRIPTOR = msg({
	message: 'All passed',
	comment: 'FYI card: line under Deploy runs, followed by the runs names. Failed runs are never in the group.',
});
const THREADS_DESCRIPTOR = msg({
	message: '{count, plural, one {# thread} other {# threads}}',
	comment: 'FYI card: how many pull requests or tasks a notification group is about.',
});
const KEPT_DESCRIPTOR = msg({
	message: 'Kept in your inbox: {items}',
	comment:
		'FYI card: the automated mail that stayed in the inbox and why. {items} is a list such as "Markus asking you on nuvio-ai #412 and the failed konvo-api deploy run".',
});
const KEPT_ASKS_ON_DESCRIPTOR = msg({
	message: '{name} asking you on {topic}',
	comment:
		'FYI card kept item: a notification that asks you something. {name} is a person or service, {topic} a pull request.',
});
const KEPT_ASKS_DESCRIPTOR = msg({
	message: '{name} asking you something',
	comment: 'FYI card kept item: a notification that asks you something. {name} is a person or service.',
});
const KEPT_FAILED_DESCRIPTOR = msg({
	message: 'the failed {name} run',
	comment:
		'FYI card kept item: a build or deploy run that failed. {name} is the workflow or service, e.g. "konvo-api deploy".',
});
const KEPT_MORE_DESCRIPTOR = msg({
	message: '{count, plural, one {# more} other {# more}}',
	comment: 'FYI card kept list: how many more mails stayed than were named.',
});
const ARCHIVE_ALL_DESCRIPTOR = msg({
	message: 'Archive all {count}',
	comment: 'FYI card: archives every FYI mail on the card. {count} is how many.',
});
const MARK_READ_DESCRIPTOR = msg({
	message: 'Mark read',
	comment: 'FYI card: marks every FYI mail read and hides the card; the mail stays in the inbox.',
});
const DISMISS_DESCRIPTOR = msg({message: 'Not now', comment: 'Accessible label of the FYI card close button.'});
const OPEN_GROUP_DESCRIPTOR = msg({
	message: '{name}: {count, plural, one {# mail} other {# mails}}',
	comment: 'Accessible label of an FYI card group row, which opens the group. {name} is the group, e.g. GitHub.',
});
const SHEET_TITLE_DESCRIPTOR = msg({
	message: '{name} · {count, plural, one {# mail} other {# mails}}',
	comment: 'Title of the FYI group sheet, e.g. "GitHub · 15 mails".',
});
const SHEET_NOTE_DESCRIPTOR = msg({
	message: 'None of these ask you anything. Mail that does stays in your inbox.',
	comment: 'FYI group sheet: why it is safe to archive the group.',
});
const READING_DESCRIPTOR = msg({
	message: 'Reading them…',
	comment: 'FYI group sheet: shown while the assistant summarises the group.',
});
const ARCHIVE_GROUP_DESCRIPTOR = msg({
	message: 'Archive {count}',
	comment: 'FYI group sheet: archives every mail in the group. {count} is how many.',
});
const KEEP_NEWEST_DESCRIPTOR = msg({
	message: 'Keep newest',
	comment: 'FYI group sheet: archives every mail in the group except the newest.',
});
const ALWAYS_TIDY_DESCRIPTOR = msg({
	message: 'Always tidy {name}',
	comment: 'FYI group sheet: opens the choices for new mail of this group. {name} is the group, e.g. GitHub.',
});
const SHOW_ON_CARD_DESCRIPTOR = msg({
	message: 'Show them on this card',
	comment: 'FYI "Always tidy" choice: new mail of the group waits on the FYI card (the default).',
});
const SHOW_ON_CARD_SUB_DESCRIPTOR = msg({
	message: 'You clear them from the card',
	comment: 'Subtext of the FYI "Show them on this card" choice.',
});
const ARCHIVE_READ_DESCRIPTOR = msg({
	message: 'Archive once read',
	comment: 'FYI "Always tidy" choice: each mail of the group leaves the inbox once it has been read.',
});
const ARCHIVE_READ_SUB_DESCRIPTOR = msg({
	message: 'Each leaves the inbox after you have read it',
	comment: 'Subtext of the FYI "Archive once read" choice.',
});
const SKIP_INBOX_DESCRIPTOR = msg({
	message: 'Skip the inbox',
	comment: 'FYI "Always tidy" choice: new mail of the group goes straight to Archive.',
});
const SKIP_INBOX_SUB_DESCRIPTOR = msg({
	message: 'Straight to Archive, still in search',
	comment: 'Subtext of the FYI "Skip the inbox" choice.',
});
const OFF_DESCRIPTOR = msg({
	message: 'Off',
	comment: 'FYI group sheet: hint beside "Always tidy" when new mail of the group simply waits on the card.',
});
const NOT_FYI_DESCRIPTOR = msg({
	message: 'Not FYI',
	comment: 'FYI group sheet: stop treating this group as FYI mail.',
});
const NOT_FYI_SUB_DESCRIPTOR = msg({
	message: 'Keep mail like this in my inbox',
	comment: 'Subtext of the FYI group sheet "Not FYI" action.',
});
const RULE_FAILED_DESCRIPTOR = msg({
	message: 'Could not save that. Try again.',
	comment: 'Toast when saving an FYI "Always tidy" or "Not FYI" choice failed.',
});

/** What the card calls a group: the service for notifications, the kind otherwise. */
export function fyiGroupName(i18n: I18n, group: Pick<MailFyiGroup, 'kind' | 'source'>): string {
	switch (group.kind) {
		case 'run':
			return i18n._(RUNS_DESCRIPTOR);
		case 'digest':
			return i18n._(DIGESTS_DESCRIPTOR);
		case 'meeting_reply':
			return i18n._(MEETING_REPLIES_DESCRIPTOR);
		default:
			return group.source;
	}
}

function listFormat(i18n: I18n, items: ReadonlyArray<string>, type: 'conjunction' | 'unit' = 'conjunction'): string {
	try {
		return new Intl.ListFormat(i18n.locale, {type, style: type === 'unit' ? 'narrow' : 'long'}).format(items);
	} catch {
		return items.join(', ');
	}
}

/** The line under a group: what it is about and who. */
export function fyiGroupLine(i18n: I18n, group: MailFyiGroup): string {
	switch (group.kind) {
		case 'run':
			return [i18n._(ALL_PASSED_DESCRIPTOR), group.topics.slice(0, 3).join(', ')].filter(Boolean).join(' · ');
		case 'digest':
			return group.sources.slice(0, 4).join(', ');
		case 'meeting_reply':
			return group.actors.slice(0, 4).join(', ');
		default: {
			const about =
				group.topics.length === 1
					? group.topics[0]
					: group.topics.length > 1
						? i18n._(THREADS_DESCRIPTOR, {count: group.topics.length})
						: null;
			// Nothing to say about it beyond the service: the newest subject says what it is.
			return (
				[about, group.actors.slice(0, 3).join(', ')].filter(Boolean).join(' · ') || (group.messages[0]?.subject ?? '')
			);
		}
	}
}

/** "Kept in your inbox: Markus asking you on nuvio-ai #412 and the failed konvo-api deploy run". */
export function fyiKeptLine(i18n: I18n, kept: ReadonlyArray<KonvoMailMessage>): string | null {
	const named = kept.slice(0, 2).flatMap((message) => {
		const attention = message.attention;
		if (!attention) return [];
		if (attention.kind === 'failed')
			return [i18n._(KEPT_FAILED_DESCRIPTOR, {name: attention.topic ?? attention.source})];
		const name = attention.actor ? firstName(attention.actor) : attention.source;
		return [
			attention.topic
				? i18n._(KEPT_ASKS_ON_DESCRIPTOR, {name, topic: attention.topic})
				: i18n._(KEPT_ASKS_DESCRIPTOR, {name}),
		];
	});
	if (named.length === 0) return null;
	const items = kept.length > 2 ? [...named, i18n._(KEPT_MORE_DESCRIPTOR, {count: kept.length - 2})] : named;
	return i18n._(KEPT_DESCRIPTOR, {items: listFormat(i18n, items)});
}

function fmtClock(iso: string | null): string {
	return iso ? new Date(iso).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'}) : '';
}

/** Group summaries already read, by group and its mails (the server keeps them an hour too). */
const summaries = new Map<string, Array<string> | null>();

function summaryKey(group: MailFyiGroup): string {
	return `${group.key}:${group.messages.map((message) => message.id).join(',')}`;
}

/** What happened in a group: the assistant's lines, else the newest few mails. */
const FyiSummary = ({group, name}: {group: MailFyiGroup; name: string}) => {
	const {i18n} = useLingui();
	const key = summaryKey(group);
	const [lines, setLines] = useState<Array<string> | null | undefined>(summaries.get(key));
	useEffect(() => {
		if (summaries.has(key)) {
			setLines(summaries.get(key));
			return;
		}
		let alive = true;
		setLines(undefined);
		void http
			.post<{lines?: Array<string>}>(Endpoints.KONVO_MAIL_FYI_SUMMARY, {
				body: {
					label: name,
					items: group.messages.slice(0, 40).map((message) => ({
						time: fmtClock(message.received_at),
						from: message.fyi?.actor ?? message.from_name ?? message.from_address ?? '',
						subject: message.subject,
						preview: message.preview,
					})),
				},
			})
			.then((response) => (Array.isArray(response.body?.lines) ? response.body.lines : null))
			.catch(() => null)
			.then((result) => {
				summaries.set(key, result);
				if (alive) setLines(result);
			});
		return () => {
			alive = false;
		};
	}, [key, group, name]);
	if (lines === undefined) {
		return (
			<p className={styles.sheetReading} data-flx="konvo-mail.fyi-sheet.reading">
				<SparkleIcon size={14} />
				{i18n._(READING_DESCRIPTOR)}
			</p>
		);
	}
	if (lines) {
		return (
			<ul className={styles.sheetLines} data-flx="konvo-mail.fyi-sheet.summary">
				{lines.map((line) => (
					<li key={line}>{line}</li>
				))}
			</ul>
		);
	}
	return (
		<ul className={styles.sheetLines} data-flx="konvo-mail.fyi-sheet.recent">
			{group.messages.slice(0, 3).map((message) => (
				<li key={message.id}>
					<span className={styles.sheetLineText}>
						{[message.fyi?.actor ? firstName(message.fyi.actor) : null, message.preview || message.subject]
							.filter(Boolean)
							.join(': ')}
					</span>
					<span className={styles.sheetLineTime}>{fmtClock(message.received_at)}</span>
				</li>
			))}
		</ul>
	);
};

interface MailFyiCardProps {
	state: MailFyiCardState;
	onArchive: (messages: ReadonlyArray<KonvoMailMessage>) => void;
	onMarkRead: (messages: ReadonlyArray<KonvoMailMessage>) => void;
	onDismiss: () => void;
	/** Selection mode: stays in place (so the list doesn't jump) but out of the way. */
	dimmed?: boolean;
}

export const MailFyiCard = observer(({state, onArchive, onMarkRead, onDismiss, dimmed = false}: MailFyiCardProps) => {
	const {i18n} = useLingui();
	const [openKey, setOpenKey] = useState<string | null>(null);
	const open = state.groups.find((group) => group.key === openKey) ?? null;
	const all = state.groups.flatMap((group) => group.messages);
	const kept = fyiKeptLine(i18n, state.kept);
	const close = () => setOpenKey(null);

	const setRule = (group: MailFyiGroup, rule: MailFyiRule | null) => {
		void KonvoCalendarPrefs.setMailFyiRule(group.key, rule).then((saved) => {
			if (!saved) ToastCommands.error(i18n._(RULE_FAILED_DESCRIPTOR));
			// The server applies the rule while it lists the inbox.
			else void KonvoMail.refresh({silent: true});
		});
	};

	const sheetGroups = (group: MailFyiGroup): Array<MenuGroupType> => {
		const name = fyiGroupName(i18n, group);
		const rule = KonvoCalendarPrefs.mailFyiRules[group.key];
		const choice = (value: MailFyiRule | null, label: string, subtext: string) => ({
			label,
			subtext,
			selected: (rule ?? null) === value,
			onSelect: () => setRule(group, value),
		});
		return [
			{
				items: [
					{
						id: 'always-tidy',
						icon: <MagicWandIcon size={20} />,
						label: i18n._(ALWAYS_TIDY_DESCRIPTOR, {name}),
						hint:
							rule === 'archive_read'
								? i18n._(ARCHIVE_READ_DESCRIPTOR)
								: rule === 'skip'
									? i18n._(SKIP_INBOX_DESCRIPTOR)
									: i18n._(OFF_DESCRIPTOR),
						items: [
							choice(null, i18n._(SHOW_ON_CARD_DESCRIPTOR), i18n._(SHOW_ON_CARD_SUB_DESCRIPTOR)),
							choice('archive_read', i18n._(ARCHIVE_READ_DESCRIPTOR), i18n._(ARCHIVE_READ_SUB_DESCRIPTOR)),
							choice('skip', i18n._(SKIP_INBOX_DESCRIPTOR), i18n._(SKIP_INBOX_SUB_DESCRIPTOR)),
						],
					},
					{
						id: 'not-fyi',
						icon: <TrayIcon size={20} />,
						label: i18n._(NOT_FYI_DESCRIPTOR),
						subtext: i18n._(NOT_FYI_SUB_DESCRIPTOR),
						onClick: () => {
							close();
							setRule(group, 'never');
						},
					},
				],
			},
		];
	};

	return (
		<section className={clsx(styles.card, dimmed && styles.dimmed)} inert={dimmed} data-flx="konvo-mail.fyi-card">
			<div className={styles.top}>
				<span className={styles.label}>
					<SparkleIcon size={14} />
					{i18n._(LABEL_DESCRIPTOR)}
				</span>
				<button
					type="button"
					className={styles.dismiss}
					aria-label={i18n._(DISMISS_DESCRIPTOR)}
					onClick={onDismiss}
					data-flx="konvo-mail.fyi-card.dismiss"
				>
					<XIcon size={16} />
				</button>
			</div>
			<div className={styles.title}>{i18n._(TITLE_DESCRIPTOR, {count: state.total})}</div>
			<div className={styles.groups}>
				{state.groups.slice(0, 4).map((group) => {
					const name = fyiGroupName(i18n, group);
					return (
						<button
							key={group.key}
							type="button"
							className={styles.group}
							onClick={() => setOpenKey(group.key)}
							aria-label={i18n._(OPEN_GROUP_DESCRIPTOR, {name, count: group.messages.length})}
							data-fyi-group={group.key}
							data-flx="konvo-mail.fyi-card.group"
						>
							<MailFyiMark kind={group.kind} source={group.source} />
							<span className={styles.groupText}>
								<span className={styles.groupName}>{name}</span>
								<span className={styles.groupLine}>{fyiGroupLine(i18n, group)}</span>
							</span>
							<span className={styles.groupCount}>{group.messages.length}</span>
							<span className={styles.groupChevron}>
								<CaretRightIcon size={16} />
							</span>
						</button>
					);
				})}
			</div>
			{kept ? (
				<div className={styles.kept} data-flx="konvo-mail.fyi-card.kept">
					<TrayIcon size={14} />
					<span>{kept}</span>
				</div>
			) : null}
			<div className={styles.actions}>
				<button
					type="button"
					className={styles.primary}
					onClick={() => onArchive(all)}
					data-flx="konvo-mail.fyi-card.archive-all"
				>
					<ArchiveIcon size={16} />
					{i18n._(ARCHIVE_ALL_DESCRIPTOR, {count: state.total})}
				</button>
				<button
					type="button"
					className={styles.secondary}
					onClick={() => onMarkRead(all)}
					data-flx="konvo-mail.fyi-card.mark-read"
				>
					{i18n._(MARK_READ_DESCRIPTOR)}
				</button>
			</div>
			{open ? (
				<MenuBottomSheet
					isOpen
					onClose={close}
					title={i18n._(SHEET_TITLE_DESCRIPTOR, {name: fyiGroupName(i18n, open), count: open.messages.length})}
					subtitle={fyiGroupLine(i18n, open)}
					leading={<MailFyiMark kind={open.kind} source={open.source} size={22} className={styles.sheetMark} />}
					headerContent={
						<div className={styles.sheetBody} data-flx="konvo-mail.fyi-sheet">
							<FyiSummary group={open} name={fyiGroupName(i18n, open)} />
							<p className={styles.sheetNote}>
								<SparkleIcon size={14} />
								{i18n._(SHEET_NOTE_DESCRIPTOR)}
							</p>
							<div className={styles.sheetButtons}>
								<button
									type="button"
									className={styles.primary}
									onClick={() => {
										close();
										onArchive(open.messages);
									}}
									data-flx="konvo-mail.fyi-sheet.archive"
								>
									<ArchiveIcon size={16} />
									{i18n._(ARCHIVE_GROUP_DESCRIPTOR, {count: open.messages.length})}
								</button>
								{open.messages.length > 1 ? (
									<button
										type="button"
										className={styles.secondary}
										onClick={() => {
											close();
											onArchive(open.messages.slice(1));
										}}
										data-flx="konvo-mail.fyi-sheet.keep-newest"
									>
										{i18n._(KEEP_NEWEST_DESCRIPTOR)}
									</button>
								) : null}
							</div>
						</div>
					}
					groups={sheetGroups(open)}
				/>
			) : null}
		</section>
	);
});
